import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { programSessionsService } from '../../../services/trainingExchangeService';
import { useToast } from '../../../contexts/ToastContext';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TrainingCalendar = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterFormat, setFilterFormat] = useState('all');
  const [selectedSession, setSelectedSession] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    setLoading(true);
    programSessionsService?.getAllWithPrograms()
      ?.then(setSessions)
      ?.catch(() => showToast?.('Failed to load sessions', 'error'))
      ?.finally(() => setLoading(false));
  }, []);

  const filteredSessions = sessions?.filter(s => filterFormat === 'all' || s?.training_programs?.format === filterFormat);

  const getSessionsForDate = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    return filteredSessions?.filter(s => s?.session_date === dateStr);
  };

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0)?.getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1)?.getDay();

  const year = currentDate?.getFullYear();
  const month = currentDate?.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const FORMAT_COLORS = {
    online: 'bg-blue-500',
    'in-person': 'bg-emerald-500',
    hybrid: 'bg-violet-500',
  };

  const upcomingSessions = filteredSessions
    ?.filter(s => new Date(s?.session_date) >= new Date())
    ?.sort((a, b) => new Date(a?.session_date) - new Date(b?.session_date))
    ?.slice(0, 8);

  return (
    <div>
      <div className="mb-20 p-16 bg-gradient-to-r from-cyan-50 to-sky-50 rounded-2xl border border-cyan-100">
        <div className="flex items-center gap-12 mb-6">
          <Icon name="CalendarDays" size={18} className="text-cyan-600" />
          <h2 className="text-base font-bold text-slate-900">Training Calendar</h2>
        </div>
        <p className="text-sm text-slate-600">Unified view of all scheduled training sessions — eCity-led and partner-led.</p>
      </div>

      {/* Legend + Filters */}
      <div className="flex flex-col sm:flex-row gap-12 items-start sm:items-center justify-between mb-20">
        <div className="flex items-center gap-16 text-xs">
          <span className="flex items-center gap-6"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />Online</span>
          <span className="flex items-center gap-6"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" />In-Person</span>
          <span className="flex items-center gap-6"><span className="w-3 h-3 rounded-full bg-violet-500 inline-block" />Hybrid</span>
        </div>
        <div className="flex gap-10">
          <select value={filterFormat} onChange={e => setFilterFormat(e?.target?.value)} className="px-14 py-9 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            {['all', 'online', 'in-person', 'hybrid']?.map(f => <option key={f} value={f}>{f === 'all' ? 'All Formats' : f?.charAt(0)?.toUpperCase() + f?.slice(1)}</option>)}
          </select>
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            {['month', 'list']?.map(v => (
              <button key={v} onClick={() => setViewMode(v)} className={`px-14 py-9 text-sm font-semibold transition-colors ${viewMode === v ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                {v?.charAt(0)?.toUpperCase() + v?.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : viewMode === 'month' ? (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-24 py-16 border-b border-slate-100">
            <button onClick={prevMonth} className="p-8 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors"><Icon name="ChevronLeft" size={18} /></button>
            <h3 className="text-base font-bold text-slate-900">{MONTH_NAMES?.[month]} {year}</h3>
            <button onClick={nextMonth} className="p-8 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors"><Icon name="ChevronRight" size={18} /></button>
          </div>
          {/* Day Names */}
          <div className="grid grid-cols-7 border-b border-slate-100">
            {DAY_NAMES?.map(d => <div key={d} className="px-8 py-10 text-center text-xs font-semibold text-slate-500">{d}</div>)}
          </div>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {Array.from({ length: firstDay }, (_, i) => <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-slate-100 bg-slate-50/50" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(year, month, day);
              const daySessions = getSessionsForDate(date);
              const isToday = date?.toDateString() === new Date()?.toDateString();
              return (
                <div key={day} className={`min-h-[80px] border-b border-r border-slate-100 p-6 ${isToday ? 'bg-cyan-50' : 'hover:bg-slate-50'} transition-colors`}>
                  <div className={`text-xs font-semibold mb-4 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-cyan-600 text-white' : 'text-slate-700'}`}>{day}</div>
                  <div className="space-y-3">
                    {daySessions?.slice(0, 2)?.map(s => (
                      <button key={s?.id} onClick={() => setSelectedSession(s)} className={`w-full text-left px-6 py-3 rounded-lg text-xs font-medium text-white truncate ${FORMAT_COLORS?.[s?.training_programs?.format] || 'bg-slate-400'}`}>
                        {s?.training_programs?.program_name}
                      </button>
                    ))}
                    {daySessions?.length > 2 && <p className="text-xs text-slate-500 pl-4">+{daySessions?.length - 2} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="space-y-12">
          {upcomingSessions?.length === 0 ? (
            <div className="text-center py-48 text-slate-500"><Icon name="CalendarDays" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">No upcoming sessions</p></div>
          ) : upcomingSessions?.map(s => (
            <div key={s?.id} className="bg-white rounded-2xl border border-slate-200 p-20 flex items-start gap-16 hover:shadow-md transition-shadow">
              <div className={`w-3 rounded-full self-stretch flex-shrink-0 ${FORMAT_COLORS?.[s?.training_programs?.format] || 'bg-slate-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-12">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">{s?.training_programs?.program_name}</h3>
                    <p className="text-xs text-slate-500 mt-2">{s?.training_programs?.partners?.brand_name || s?.training_programs?.partners?.legal_name || 'eCity-led'}</p>
                  </div>
                  <span className={`px-10 py-4 text-xs font-semibold rounded-full flex-shrink-0 ${s?.session_status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                    {s?.session_status?.charAt(0)?.toUpperCase() + s?.session_status?.slice(1)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-16 mt-10 text-xs text-slate-500">
                  <span className="flex items-center gap-6"><Icon name="Calendar" size={13} />{s?.session_date ? new Date(s?.session_date)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—'}</span>
                  {s?.start_time && <span className="flex items-center gap-6"><Icon name="Clock" size={13} />{s?.start_time}{s?.end_time ? ` – ${s?.end_time}` : ''}</span>}
                  {s?.location && <span className="flex items-center gap-6"><Icon name="MapPin" size={13} />{s?.location}</span>}
                  <span className="flex items-center gap-6"><Icon name="Users" size={13} />{s?.seats_available} seats</span>
                  {s?.trainer_name && <span className="flex items-center gap-6"><Icon name="User" size={13} />{s?.trainer_name}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedSession(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-cyan-50 to-sky-50">
              <div className="flex items-center gap-12">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center">
                  <Icon name="CalendarDays" size={18} className="text-white" />
                </div>
                <h2 className="text-sm font-bold text-slate-900">Session Details</h2>
              </div>
              <button onClick={() => setSelectedSession(null)} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
            </div>
            <div className="p-24 space-y-14">
              <div>
                <h3 className="font-bold text-slate-900 text-base">{selectedSession?.training_programs?.program_name}</h3>
                <p className="text-sm text-slate-500 mt-4">{selectedSession?.training_programs?.partners?.brand_name || selectedSession?.training_programs?.partners?.legal_name || 'eCity-led'}</p>
              </div>
              <div className="grid grid-cols-2 gap-12 text-sm">
                <div><p className="text-xs font-semibold text-slate-500 mb-4">Date</p><p className="text-slate-900">{selectedSession?.session_date ? new Date(selectedSession?.session_date)?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</p></div>
                <div><p className="text-xs font-semibold text-slate-500 mb-4">Time</p><p className="text-slate-900">{selectedSession?.start_time || '—'}{selectedSession?.end_time ? ` – ${selectedSession?.end_time}` : ''}</p></div>
                <div><p className="text-xs font-semibold text-slate-500 mb-4">Location</p><p className="text-slate-900">{selectedSession?.location || '—'}</p></div>
                <div><p className="text-xs font-semibold text-slate-500 mb-4">Seats Available</p><p className="text-slate-900">{selectedSession?.seats_available}</p></div>
                {selectedSession?.trainer_name && <div><p className="text-xs font-semibold text-slate-500 mb-4">Trainer</p><p className="text-slate-900">{selectedSession?.trainer_name}</p></div>}
                {selectedSession?.trainer_role && <div><p className="text-xs font-semibold text-slate-500 mb-4">Trainer Role</p><p className="text-slate-900">{selectedSession?.trainer_role}</p></div>}
              </div>
              {selectedSession?.meeting_link && (
                <a href={selectedSession?.meeting_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-8 px-16 py-10 text-sm font-semibold text-cyan-700 bg-cyan-50 rounded-xl hover:bg-cyan-100 transition-colors">
                  <Icon name="ExternalLink" size={15} />Join Meeting
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingCalendar;
